import {useSyncExternalStore} from 'react';

// Module-scoped filter store (global-filter-store-pattern) — the Filter button with its
// active-count badge, the drawer and the list hook observe one filter. Backend-supported
// fields (UsageEventsFilterInput, FNP-594):
//   accountId          -> ID
//   customerId         -> ID
//   meterCode          -> String
//   unresolvedCustomer -> Boolean ("true" selects the who-pays-unknown rows)
//   unmetered          -> Boolean ("true" selects the unknown-meter rows)
// The Booleans travel as the string "true"; AppSync coerces it (measured on dev,
// FNP-1883). No default filter is seeded — admins see all events newest-first.
//
// useSyncExternalStore, not useState(globalFilters) + an effect-registered listener: the
// old shape could miss a change fired between a consumer's first render and its effect
// commit (FNP-605). getSnapshot returns the same reference until a change swaps it.
let globalFilters: Record<string, string> = {};
const listeners: Set<() => void> = new Set();

const notify = (filters: Record<string, string>) => {
    globalFilters = filters;
    listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getSnapshot = () => globalFilters;

// Test-only: the store outlives a test case, so tests reset it or leak state.
export const __resetUsageEventsFilterForTesting = () => {
    notify({});
};

export const useUsageEventsFilter = () => {
    const filters = useSyncExternalStore(subscribe, getSnapshot);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notify(newFilters);
    };

    const handleClearFilters = () => {
        notify({});
    };

    return {
        filters,
        activeCount: Object.keys(filters).length,
        handleSetFilters,
        handleClearFilters,
    };
};
